/**
 * @module app.core
 */

import { apiCall } from 'app/shared/lib/essette-api';
import { autoinject, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { ExtendLocalState } from 'app/shared/models/local-state-extensions';
import { getReference } from 'app/shared/models/essette-types';
import { guid } from 'app/shared/lib/guid';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { ProviderLocationSelect } from 'app/cd/elements/providers/provider-location-select';
import { ProviderService } from 'app/cd/services/provider-service';
import { Toaster } from 'app/shared/lib/toaster';
import { UserService } from 'app/core/services/user-service';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

type ProviderLocationWithLocalState = ExtendLocalState<CD.ProviderLocationUserDetailModel, { isSelected?: boolean; }>;

// Parameters for activating/deactivating an associated provider
type IActivateAssociatedProvider = {
    associatedProvider: CD.ProviderUserContext;
    isActive: boolean;
};

@autoinject()
export class SaveAssociatedProvider extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    protected providerId: string | null = null;
    protected providerName: string | null = null;
    @bindable.object()
    public user: Core.User | null = null;
    @observable
    public associatedProviderLocation: CD.ProviderLocation | null = null;
    protected providerLocation: CD.ProviderLocation | null = null;
    protected id: guid = guid();

    @bindable.function()
    protected activateAssociatedProvider: Function | ((params: IActivateAssociatedProvider) => boolean) = () => { return false; };

    private allHomePages: ReadonlyArray<Core.NavigationHomePage> | null = null;
    protected filteredHomePages: ReadonlyArray<Core.NavigationHomePage> | null = null;
    protected selectedHomePage: Core.NavigationHomePage | null = null;

    protected roles: Array<Core.UserRole> | null = null;
    protected selectedRoles: Array<Core.UserRole> = [];

    public context: CD.ProviderUserContext | null = null;
    public editContext: CD.ProviderUserContext | null = null;
    protected locations: Array<ProviderLocationWithLocalState> | null = null;
    protected selectedLocations: Array<ProviderLocationWithLocalState> = [];

    protected matcher = modelIdMatcher;
    protected portalApplicationId: string | null = null;
    protected primaryUser: CD.ProviderUser | null = null;
    protected loginChoiceDisabled: boolean = false;
    protected loginChoice: "primary" | "proxy" | null = "proxy";

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly toaster: Toaster,
        private readonly userService: UserService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async bind(): Promise<void> {
        if(!this.roles) {
            if(!this.portalApplicationId) { await this.loadPortalAppId(); }
            const roles = await this.loadProviderUserRoles();
            this.roles = roles;
        }
    }

    ////****************** Element References ***************************************////
    protected providerSelect: ProviderLocationSelect | null = null;

    ////****************** Events ***************************************************////
    // search & save mode : provider details come from the searched/selected provider and its context
    // model: CD.ProviderLocation
    protected async associatedProviderLocationChanged() {
        this.reset(); // clears out form prior to selecting provider; and also after removing selected provider
        if(this.associatedProviderLocation?.id) {
            this.providerLocation = this.associatedProviderLocation;
            await this.loadProviderContext();
            this.loadAssociatedProviderDataByContext(this.providerLocation);
        }
    }

    // edit mode : provider details come from (parent) associated provider table
    // model: CD.ProviderUserContext
    public async loadAssociatedProviderDataByEditContext() {
        if(!this.editContext) { return; }
        this.providerLocation = this.editContext.providerLocations?.[0] || null;
        this.providerId = this.editContext.provider?.identifier?.primary || null;
        this.providerName = this.editContext.provider?.name?.full || null;
        if(this.editContext.provider?.detail?.isPrimary) { this.loginChoice = "primary"; }
        if(this.editContext.provider?.primaryUser?.id) {
            if(this.editContext.provider?.primaryUser?.id === this.user?.id) {
                this.loginChoice = "primary";
                this.loginChoiceDisabled = false;
            } else {
                this.loginChoiceDisabled = true;
                this.loginChoice = "proxy";
                this.primaryUser = this.editContext.provider.primaryUser;
            }
        }
        this.selectedHomePage = this.editContext?.homePage || null;
        const [userLocations, providerLocations, providerLocationsRoles] = await Promise.all(
            [
                this.loadProviderUserLocations(this.editContext.provider?.id as string),
                this.editContext?.providerLocations || [],
                !!this.editContext?.roles ? this.editContext.roles : []
            ]
        );
        this.matchLocations(userLocations, providerLocations);
        this.selectedRoles.push(...providerLocationsRoles);
        if(!this.portalApplicationId) { await this.loadPortalAppId(); }
        await this.loadHomePages();
        this.updateHomePages();

    }

    protected async loadAssociatedProviderDataByContext(providerLocation: CD.ProviderLocation) {
        this.providerId = providerLocation.provider?.identifier?.primary || null;
        this.providerName = providerLocation.provider?.name?.full || null;
        if(providerLocation.provider?.primaryUser?.id) {
            if(providerLocation.provider?.primaryUser?.id === this.user?.id) {
                this.loginChoice = "primary";
                this.loginChoiceDisabled = false;
            } else {
                this.loginChoiceDisabled = true;
                this.loginChoice = "proxy";
                this.primaryUser = providerLocation.provider.primaryUser;
            }
        }
        this.selectedHomePage = this.context?.homePage || null;
        const [userLocations, providerLocations, providerLocationsRoles] = await Promise.all(
            [
                this.loadProviderUserLocations(providerLocation.provider?.id as string),
                this.context?.providerLocations || [],
                !!this.context?.roles ? this.context.roles : []
            ]
        );
        this.matchLocations(userLocations, providerLocations);
        this.selectedRoles.push(...providerLocationsRoles);
        if(!this.portalApplicationId) { await this.loadPortalAppId(); }
        await this.loadHomePages();
        this.updateHomePages();
    }

    ////****************** Functions ************************************************////

    /**Updates filteredHomePages to only home pages that exist for selectedRoles */
    protected updateHomePages(): void {
        if(!this.allHomePages) { return; }
        this.filteredHomePages = this.allHomePages.filter((page) => {
            return page.roles?.some((r) => this.selectedRoles.find((sr) => sr.id === r.id)) || page.flag?.allUsers;
        });
    }

    protected updateLocations(): void {
        if(!this.locations) { return; }

        this.locations.forEach((l) => {
            if(this.selectedLocations.some((sl) => sl.id === l.id)) {
                l._state!.isSelected = true;
            } else {
                l._state = { isSelected: false };
            }
        });
    }

    private matchLocations(userLocations: ProviderLocationWithLocalState[], providerLocations: ProviderLocationWithLocalState[]): void {
        // Reset location detail and local state properties
        userLocations.forEach((location) => {
            location.detail = undefined;
            location._state = { isSelected: false };
        });

        providerLocations.forEach((il) => {
            const location = userLocations.find((r) => r.id === il.id);
            if(location) {
                location._state = { isSelected: true };
                location.detail = il.detail;
                this.selectedLocations.push(location);
            }
        });
    }

    protected buildProvider(): CD.ProviderUserContext {
        const saveProvider: CD.ProviderUserContext = {
            id: this.editContext?.id || null,
            provider: {
                id: this.associatedProviderLocation?.provider?.id || this.editContext?.provider?.id || null,
                detail: {
                    isPrimary: this.loginChoice === "primary",
                },
                locations: this.selectedLocations.map(location => {
                    return {
                        id: location.id, detail: !location.detail ? undefined :
                            {
                                associatedContact: getReference(location.detail?.associatedContact)
                            }
                    };
                })
            },
            homePage: {
                id: this.selectedHomePage?.id || null,
            },
            roles: this.selectedRoles.map(role => {
                return { id: role.id }
            }),
            user: {
                id: this.user?.id || null
            },
            application: { code: 'Portal.Providers' }
        };

        return saveProvider;
    }

    ////****************** IForm ****************************************************////
    public reset(): void {
        this.context = null;
        this.providerId = null;
        this.providerName = null;
        this.primaryUser = null;
        this.loginChoiceDisabled = false;
        this.loginChoice = "proxy";
        this.locations = []
        this.selectedLocations = [];
        this.selectedRoles = [];
        this.selectedHomePage = null;
        this.filteredHomePages = null;
    }

    public resetProvider(): void {
        this.editContext = null;
        this.associatedProviderLocation = null;
        this.providerLocation = null;
        if(this.providerSelect) this.providerSelect.clear();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        let isSuccess = true;
        if(this.editContext && !this.editContext.isActive) {
            isSuccess = await this.activateAssociatedProvider({ associatedProvider: this.editContext, isActive: true });
        }
        if(isSuccess) {
            isSuccess = await this.saveProvider();
        }
        return isSuccess;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHomePages(): Promise<void> {
        if(!this.portalApplicationId) { return; }
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = ['roles'];

        try {
            this.state.beginLoad();
            const response = await this.providerService.loadProviderPortalHomePages(this.portalApplicationId, options, includes);
            this.allHomePages = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPortalAppId(): Promise<void> {
        if(!this.portalApplicationId) {
            const response = await this.providerService.loadPortalApp('Portal.Providers');
            this.portalApplicationId = response.results[0].id || null;
        }
    }

    @apiCall
    private async loadProviderContext(): Promise<void> {
        if(!this.associatedProviderLocation?.provider?.id) return;
        const fields: string[] = [
            'provider.detail.isPrimary',
            'roles.$display',
            'homePage.$display',
        ];

        const includes: string[] = [
            'provider',
            'providerLocations',
            'homepage',
            'roles',
        ];

        try {
            this.state.beginLoad();
            const response = await this.userService.loadAssociatedProviderContext(this.associatedProviderLocation.provider.id, this.user?.id as string, undefined, includes, fields);
            this.context = response.results[0] || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviderUserLocations(providerId: string): Promise<ProviderLocationWithLocalState[]> {
        if(!providerId) { return []; }
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = ['facility', 'contacts', 'contacts.primaryUser'];

        try {
            this.state.beginLoad();

            const response = await this.providerService.loadProviderLocations(providerId, options, includes);
            this.locations = response.results;
            return this.locations;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProviderUserRoles(): Promise<Core.UserRole[]> {
        try {
            this.state.beginLoad();

            const response = await this.providerService.loadProviderPortalUserRoles(this.portalApplicationId as string);
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveProvider(): Promise<boolean> {
        const insertProvider: CD.Provider = this.buildProvider();

        let isSuccessful = false;
        let errorMessage: string = "";
        try {
            this.state.beginSave();

            const response = await this.userService.saveAssociatedProvider(insertProvider);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            this.toaster.standard.saved('cd:provider.associated.$', false);
            throw error;
        } finally {
            if(isSuccessful) {
                if(this.editContext?.id) {
                    this.toaster.standard.updated('cd:provider.associated.$', isSuccessful);
                } else {
                    this.toaster.standard.created('cd:provider.associated.$', isSuccessful);
                }
                this.reset();
                this.resetProvider();
            } else {
                const exception = (this.toaster.toast())
                    .title('Error')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();

        }
    }
}
